import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API } from "../../lib/api";
import { ArrowLeft, Play, Trash2, Share2, Cpu, Sparkles, GitFork, ShieldCheck, RotateCw, Star, UserPlus, UserCheck } from "lucide-react";

/**
 * Model Detail — view + test + grade + fork + follow-author + star any model.
 */
export default function StudioModel() {
  const { id } = useParams();
  const nav = useNavigate();
  const [model, setModel] = useState(null);
  const [err, setErr] = useState("");
  const [prompt, setPrompt] = useState("Introduce yourself in two sentences.");
  const [out, setOut] = useState("");
  const [running, setRunning] = useState(false);
  const [tab, setTab] = useState("test");
  const [grading, setGrading] = useState(false);
  const [gradeReport, setGradeReport] = useState(null);
  const [forking, setForking] = useState(false);
  const [availableBrains, setAvailableBrains] = useState([]);
  const [graderBrain, setGraderBrain] = useState("");
  const [starState, setStarState] = useState({ starred: false, star_count: 0 });
  const [followState, setFollowState] = useState({ is_following: false, followers: 0 });

  const isOwner = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "null");
      return !!u && model && u.user_id === model.user_id;
    } catch { return false; }
  })();

  useEffect(() => {
    api.get(`/ai-models/${id}`)
      .then((r) => setModel(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Model not found"));
  }, [id]);

  useEffect(() => {
    // Load catalog brains once for the grader dropdown
    api.get("/models").then((r) => setAvailableBrains((r.data.models || []).filter((b) => (b.capabilities || []).includes("chat") || !b.capabilities)));
  }, []);

  useEffect(() => {
    // Star & follow status — only meaningful once model is loaded
    if (!id) return;
    api.get(`/ai-models/${id}/star`).then((r) => setStarState(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!model?.user_id || isOwner) return;
    api.get(`/follow/status/${model.user_id}`).then((r) => setFollowState(r.data)).catch(() => {});
  }, [model?.user_id, isOwner]);

  const toggleStar = async () => {
    try {
      if (starState.starred) {
        await api.delete(`/ai-models/${id}/star`);
        setStarState((s) => ({ starred: false, star_count: Math.max(0, s.star_count - 1) }));
      } else {
        await api.post(`/ai-models/${id}/star`);
        setStarState((s) => ({ starred: true, star_count: s.star_count + 1 }));
      }
    } catch (e) { alert(e.response?.data?.detail || "Star failed"); }
  };

  const toggleFollow = async () => {
    if (!model?.user_id) return;
    try {
      if (followState.is_following) {
        await api.delete(`/follow/${model.user_id}`);
        setFollowState((f) => ({ ...f, is_following: false, followers: Math.max(0, f.followers - 1) }));
      } else {
        await api.post(`/follow/${model.user_id}`);
        setFollowState((f) => ({ ...f, is_following: true, followers: f.followers + 1 }));
      }
    } catch (e) { alert(e.response?.data?.detail || "Follow failed"); }
  };

  const test = async () => {
    setRunning(true);
    setOut("");
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/ai-models/${id}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ prompt }),
      });
      if (!res.body) throw new Error("No stream body");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data:\s*/, "").trim();
          if (!line || line === "[DONE]") continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === "delta" && ev.content) setOut((t) => t + ev.content);
            if (ev.type === "error") setOut((t) => t + `\n\n⚠️ ${ev.error}`);
          } catch { /* ignore */ }
        }
      }
    } catch (e) {
      setOut(`⚠️ ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const remove = async () => {
    if (!confirm("Delete this model?")) return;
    try {
      await api.delete(`/ai-models/${id}`);
      nav("/studio");
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  const togglePublic = async () => {
    try {
      const r = await api.patch(`/ai-models/${id}`, { is_public: !model.is_public });
      setModel(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
    }
  };

  const fork = async () => {
    setForking(true);
    try {
      const r = await api.post(`/ai-models/${id}/fork`);
      nav(`/studio/${r.data.ai_model_id}`);
    } catch (e) {
      alert(e.response?.data?.detail || "Fork failed");
    } finally {
      setForking(false);
    }
  };

  const runGrade = async () => {
    setGrading(true);
    setGradeReport(null);
    try {
      const body = graderBrain ? { grader_brain_id: graderBrain } : {};
      const r = await api.post(`/ai-models/${id}/grade`, body);
      setGradeReport(r.data);
      // refresh cached grade on the model card
      const m = await api.get(`/ai-models/${id}`);
      setModel(m.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Grade failed");
    } finally {
      setGrading(false);
    }
  };

  if (err) return <div className="max-w-3xl mx-auto p-8"><div className="brut-card p-6 text-red-600 text-sm">{err}</div></div>;
  if (!model) return <div className="max-w-3xl mx-auto p-8 italic text-slate-400">Loading…</div>;

  const kindColor = model.kind === "composed" ? "#F59E0B" : "#EA580C";
  const brainRef = model.kind === "composed" ? model.base_brain_id : model.hf_repo_id;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <Link to="/studio" className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm font-bold" data-testid="back-to-studio">
        <ArrowLeft size={14} strokeWidth={2.5} /> Studio
      </Link>

      {/* Header */}
      <div className="brut-card p-6" style={{ boxShadow: `6px 6px 0 ${kindColor}` }} data-testid="model-header">
        <div className="flex items-start gap-4">
          <div
            className="w-20 h-20 rounded-2xl border-2 border-slate-900 flex items-center justify-center text-5xl shrink-0"
            style={{ backgroundColor: kindColor + "22" }}
          >
            {model.avatar}
          </div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-heading font-bold px-1.5 py-0.5 rounded-full border-2 mb-1"
                 style={{ borderColor: kindColor, color: kindColor, backgroundColor: kindColor + "1F" }}>
              {model.kind === "composed" ? "Composed" : "HF Import"}
            </div>
            <h1 className="font-heading text-3xl font-bold" data-testid="model-name">{model.name}</h1>
            <div className="text-xs font-mono text-slate-500 mt-1">{brainRef}</div>
            {model.forked_from && (
              <Link to={`/studio/${model.forked_from}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-heading font-bold uppercase tracking-widest text-amber-700 hover:text-amber-900" data-testid="lineage-badge">
                <GitFork size={11} strokeWidth={3} /> Forked from {model.forked_from_name || "another model"} · @{model.forked_from_author || "someone"}
              </Link>
            )}
            {model.description && <p className="text-sm text-slate-600 mt-3">{model.description}</p>}
            <div className="flex flex-wrap gap-2 mt-3">
              {(model.tags || []).map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-600">{t}</span>
              ))}
              {model.license && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800">{model.license}</span>}
              {model.fork_count > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-900 text-white font-heading font-bold uppercase tracking-widest inline-flex items-center gap-1" data-testid="fork-count">
                  <GitFork size={9} strokeWidth={3} /> {model.fork_count} fork{model.fork_count === 1 ? "" : "s"}
                </span>
              )}
              {model.last_grade && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-heading font-bold uppercase tracking-widest inline-flex items-center gap-1"
                      style={{ backgroundColor: gradeColor(model.last_grade.average.overall) + "22", color: gradeColor(model.last_grade.average.overall), border: `1px solid ${gradeColor(model.last_grade.average.overall)}` }}
                      data-testid="last-grade-badge">
                  <ShieldCheck size={9} strokeWidth={3} /> Grade {model.last_grade.average.overall}
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {/* Star is available to everyone (owner OR viewer) */}
            <button onClick={toggleStar} className={`brut-btn text-xs ${starState.starred ? "bg-amber-200 text-slate-900" : ""}`} data-testid="toggle-star">
              <Star size={11} strokeWidth={3} className={starState.starred ? "fill-slate-900" : ""} />
              {starState.starred ? "Starred" : "Star"} {starState.star_count > 0 && `· ${starState.star_count}`}
            </button>
            {isOwner ? (
              <>
                <button onClick={togglePublic} className="brut-btn text-xs" data-testid="toggle-public">
                  <Share2 size={11} strokeWidth={3} /> {model.is_public ? "Public" : "Private"}
                </button>
                <button onClick={remove} className="brut-btn text-xs bg-red-500 text-white" data-testid="delete-model">
                  <Trash2 size={11} strokeWidth={3} /> Delete
                </button>
              </>
            ) : (
              <>
                {model.user_id && (
                  <button onClick={toggleFollow} className={`brut-btn text-xs ${followState.is_following ? "bg-slate-900 text-white" : ""}`} data-testid="toggle-follow">
                    {followState.is_following ? <><UserCheck size={11} strokeWidth={3} /> Following</> : <><UserPlus size={11} strokeWidth={3} /> Follow author</>}
                  </button>
                )}
                {model.kind === "composed" && (
                  <button onClick={fork} disabled={forking} className="brut-btn text-xs" data-testid="fork-model">
                    <GitFork size={11} strokeWidth={3} /> {forking ? "Forking…" : "Fork to my Studio"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Config panel (composed only) */}
      {model.kind === "composed" && (
        <div className="brut-card p-5 space-y-3" data-testid="config-panel">
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500">System prompt</div>
          <pre className="text-sm bg-slate-50 border-2 border-slate-200 rounded-lg p-3 whitespace-pre-wrap font-mono">{model.system_prompt}</pre>
          <div className="flex flex-wrap gap-3 text-xs">
            <Stat k="Tone" v={model.tone} />
            <Stat k="Temperature" v={model.temperature} />
            <Stat k="Max tokens" v={model.max_tokens} />
            <Stat k="Few-shot" v={(model.few_shot || []).length} />
          </div>
          {(model.few_shot || []).length > 0 && (
            <details className="border-2 border-slate-200 rounded-xl p-3">
              <summary className="cursor-pointer text-xs uppercase tracking-widest font-heading font-bold text-slate-600">Few-shot examples ({model.few_shot.length})</summary>
              <div className="mt-3 space-y-3">
                {model.few_shot.map((p, i) => (
                  <div key={`fewshot-${i}-${(p.user || "").slice(0, 15)}`} className="text-sm">
                    <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">Example {i + 1}</div>
                    <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-2 mb-1"><span className="text-[10px] font-bold text-slate-500">USER:</span> {p.user}</div>
                    <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-2"><span className="text-[10px] font-bold text-amber-700">MODEL:</span> {p.assistant}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="model-tabs">
        <TabBtn active={tab === "test"} onClick={() => setTab("test")} icon={Play} label="Test" testId="tab-test" />
        {model.kind === "composed" && (
          <TabBtn active={tab === "grade"} onClick={() => setTab("grade")} icon={ShieldCheck} label={model.last_grade ? `Grade ${model.last_grade.average.overall}` : "Grade"} testId="tab-grade" />
        )}
      </div>

      {tab === "test" && (
        <div className="brut-card p-5 space-y-3" data-testid="test-panel">
          <div className="flex items-center gap-2">
            <Sparkles size={16} strokeWidth={2.5} />
            <h3 className="font-heading text-xl font-bold">Try it</h3>
          </div>
          <textarea className="brut-input w-full h-20" value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="test-prompt" />
          <button onClick={test} disabled={running} className="brut-btn text-sm" data-testid="run-test">
            <Play size={12} strokeWidth={3} /> {running ? "Streaming…" : "Run"}
          </button>
          {(out || running) && (
            <div className="brut-card bg-slate-50 p-4 text-sm whitespace-pre-wrap font-mono min-h-[80px]" data-testid="test-output">
              {out || <span className="italic text-slate-400">Waiting for first token…</span>}
            </div>
          )}
        </div>
      )}

      {tab === "grade" && model.kind === "composed" && (
        <div className="brut-card p-5 space-y-4" data-testid="grade-panel">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} strokeWidth={2.5} />
              <h3 className="font-heading text-xl font-bold">Guardrail grading</h3>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">Judge</label>
              <select
                className="brut-input text-xs py-1"
                value={graderBrain}
                onChange={(e) => setGraderBrain(e.target.value)}
                disabled={grading}
                data-testid="grader-select"
              >
                <option value="">Self ({model.base_brain_id})</option>
                {availableBrains.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <button onClick={runGrade} disabled={grading} className="brut-btn text-sm" data-testid="run-grade">
                <RotateCw size={12} strokeWidth={3} className={grading ? "animate-spin" : ""} /> {grading ? "Grading…" : "Run grading"}
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Five canned prompts stream through your model. The chosen judge scores each response against your style, format, tone, and banned-word rules on 0-100.
            Banned words are checked deterministically. Pick a different judge to avoid the model self-flattering.
          </p>

          {!gradeReport && !grading && model.last_grade && (
            <div className="brut-card bg-slate-50 p-4">
              <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-2">Last grade · {new Date(model.last_grade.graded_at).toLocaleString()}</div>
              <ScorePills scores={model.last_grade.average} />
            </div>
          )}

          {gradeReport && (
            <div className="space-y-4" data-testid="grade-report">
              <div className="brut-card p-4" style={{ boxShadow: `4px 4px 0 ${gradeColor(gradeReport.average.overall)}` }}>
                <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-2">Average across {gradeReport.results.length} prompts</div>
                <ScorePills scores={gradeReport.average} />
              </div>
              {gradeReport.results.map((r, i) => (
                <details key={r.prompt_id || `${(r.prompt || "").slice(0, 20)}-${i}`} className="brut-card p-4">
                  <summary className="cursor-pointer flex items-center gap-2 text-sm">
                    <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-heading font-bold`} style={{ backgroundColor: gradeColor(r.scores.overall), color: "white" }}>
                      {r.scores.overall}
                    </span>
                    <span className="font-heading font-bold truncate">{r.prompt}</span>
                  </summary>
                  <div className="mt-3 space-y-2">
                    <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">Model response</div>
                    <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-2 text-sm whitespace-pre-wrap">{r.response}</div>
                    <ScorePills scores={r.scores} />
                    {r.scores.notes && <div className="text-xs italic text-slate-600">{r.scores.notes}</div>}
                  </div>
                </details>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Usage hint */}
      <div className="brut-card p-5 bg-amber-50" data-testid="usage-hint">
        <div className="flex items-start gap-3">
          <Cpu size={20} strokeWidth={2.5} className="mt-1 shrink-0" />
          <div>
            <h4 className="font-heading font-bold text-base mb-1">Use it anywhere</h4>
            <p className="text-sm text-slate-700">
              This model is available across the platform as brain id <code className="bg-white border border-slate-300 rounded px-1 py-0.5 font-mono text-xs">{model.brain_id}</code>.
              Pick it in the Compare panel, add it as a Debate participant, or set it as the brain on any Worker or Workflow node.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 text-xs">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{k}</span>
      <span className="font-mono text-slate-900">{String(v ?? "—")}</span>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-slate-900 font-heading font-bold text-xs uppercase tracking-widest transition-colors ${active ? "bg-slate-900 text-white" : "bg-white text-slate-900 hover:bg-amber-50"}`}
      data-testid={testId}
    >
      <Icon size={12} strokeWidth={2.5} /> {label}
    </button>
  );
}

function ScorePills({ scores }) {
  const rows = [
    ["Overall", scores.overall],
    ["Style", scores.style],
    ["Format", scores.format],
    ["Banned", scores.banned],
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {rows.map(([label, val]) => (
        <div key={label} className="inline-flex items-center gap-1.5 border-2 border-slate-900 rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: gradeColor(val) + "22", color: "#0f172a" }}>
          <span className="text-[10px] uppercase tracking-widest font-heading font-bold" style={{ color: gradeColor(val) }}>{label}</span>
          <span className="font-mono font-bold">{val}</span>
        </div>
      ))}
    </div>
  );
}

function gradeColor(v) {
  if (v >= 80) return "#10B981"; // green
  if (v >= 50) return "#F59E0B"; // amber
  return "#EF4444"; // red
}
